import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    // Disparar evento personalizado para reinicializar componentes del carrito
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('cart:open'));
      console.log('🛒 Evento cart:open disparado');
    }, 100);

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

// Función helper para reintentar hasta que las funciones estén disponibles
function tryInitCartComponents(maxRetries = 15, delay = 150) {
  let retries = 0;

  function attempt() {
    console.log(`🔄 Intento ${retries + 1}/${maxRetries} de inicializar componentes del carrito`);

    // Verificar que los elementos del DOM existan antes de inicializar
    const deliveryOptions = document.querySelectorAll('.vj-delivery-option');
    const deliveryBadges = document.querySelectorAll('.cart-delivery-badge');

    console.log(`   - Opciones de delivery encontradas: ${deliveryOptions.length}`);
    console.log(`   - Badges de delivery encontrados: ${deliveryBadges.length}`);

    let selectorInit = false;
    let badgeInit = false;

    // Solo inicializar si hay elementos en el DOM
    if (typeof window.initCartDeliverySelector === 'function' && deliveryOptions.length > 0) {
      console.log('✅ Llamando a initCartDeliverySelector');
      try {
        window.initCartDeliverySelector();
        selectorInit = true;
      } catch (e) {
        console.error('❌ Error en initCartDeliverySelector:', e);
      }
    }

    if (typeof window.initCartDeliveryBadge === 'function' && deliveryBadges.length > 0) {
      console.log('✅ Llamando a initCartDeliveryBadge');
      try {
        window.initCartDeliveryBadge();
        badgeInit = true;
      } catch (e) {
        console.error('❌ Error en initCartDeliveryBadge:', e);
      }
    }

    // Si ambas funciones se ejecutaron (o no hay elementos para inicializar), terminamos
    const selectorOk = selectorInit || deliveryOptions.length === 0;
    const badgeOk = badgeInit || deliveryBadges.length === 0;

    if (selectorOk && badgeOk) {
      console.log('✅ Todos los componentes inicializados correctamente');
      return;
    }

    // Si no están disponibles y aún tenemos reintentos, intentar de nuevo
    retries++;
    if (retries < maxRetries) {
      console.log(`⏳ Elementos o funciones aún no disponibles, reintentando en ${delay}ms...`);
      setTimeout(attempt, delay);
    } else {
      console.warn('⚠️ Máximo de reintentos alcanzado. Estado final:');
      console.warn(`   - initCartDeliverySelector: ${typeof window.initCartDeliverySelector}, elementos: ${deliveryOptions.length}`);
      console.warn(`   - initCartDeliveryBadge: ${typeof window.initCartDeliveryBadge}, elementos: ${deliveryBadges.length}`);
    }
  }

  attempt();
}

// Listeners globales para reinicializar componentes del carrito cuando el HTML se actualiza
document.addEventListener('cart:html-updated', () => {
  console.log('🔄 [cart-drawer.js] Evento cart:html-updated recibido');
  // Dar tiempo suficiente para que morphSection termine de actualizar el DOM
  setTimeout(() => tryInitCartComponents(), 300);
});

document.addEventListener('cart:open', () => {
  console.log('🛒 [cart-drawer.js] Evento cart:open recibido');
  setTimeout(() => tryInitCartComponents(), 300);
});

// También escuchar el evento cart:update (CartAddEvent y CartUpdateEvent usan este nombre)
document.addEventListener('cart:update', () => {
  console.log('🛒 [cart-drawer.js] Evento cart:update recibido');
  setTimeout(() => tryInitCartComponents(), 500);
});

console.log('✅ [cart-drawer.js] Listeners globales registrados');
